import {transparentize} from 'polished';
import React, {FC, useLayoutEffect, useRef, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useLazyUnmountCancelToken} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ClickEventHandler} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
  combineCancelTokens,
} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {
  attachmentPreviewableImageTypes,
  AttachmentStatusesEnum,
  AttachmentVisualContentTypesEnum,
} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {AttachmentBasePublicProps} from '../../../../../../libs/shared/ui/src/components/attachments/attachmentBasePublicProps';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {FileSizeFormatter} from '../../../../../../libs/shared/ui/src/components/formatters/fileSizeFormatter';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipExplicitCondition} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipExplicitCondition';
import {Skeleton} from '../../../../../../libs/shared/ui/src/components/utility/skeleton';
import {VisuallyHidden} from '../../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {computeRemoteAttachmentSourceName} from '../../../../../../srcCommon/helpers/models/attachmentHelpers';
import thumbnailPlaceholderImageContent from '../../../../../assets/images/genericFile.svg';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {
  DownloadAttachmentOptions,
  DownloadAttachmentResult,
  DownloadStatusesEnum,
  PreviewAttachmentOptions,
} from '../../../../../services/browser';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {CommentViewerSingleImageAttachmentContainer} from '../comment/commentViewerSingleImageAttachmentContainer';
import {AttachmentDragSource} from './attachmentDragSource';
import {AttachmentFileIcon} from './attachmentFileIcon';

// Line height and max visible lines for attachments
const attachmentLineHeight = 20;
const attachmentMaxMultiLineRows = 4;
export const largeAttachmentHeight = 114;
export const attachmentHeight = 46;

/*
 * Props.
 */

interface AttachmentBaseProps extends AttachmentBasePublicProps {
  onPreviewClickAsync: (options: PreviewAttachmentOptions) => Promise<void>;
  onDownloadClickAsync: (options: DownloadAttachmentOptions) => Promise<DownloadAttachmentResult>;
}

interface AttachmentBaseStyleProps {
  $hasError?: boolean;
  $hasLargeAttachmentView?: boolean;
  $newMultiAttachmentStyle?: boolean;
}

/*
 * Style.
 */

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  flex-grow: 1;
  display: flex;
`;

const StyledAttachmentDragSource = styled(AttachmentDragSource)`
  flex-grow: 1;
  display: flex;
`;

const commonMultiAttachmentStyles = css`
  border-radius: 8px;
  background: ${(p) => p.theme.alphas.black30};
`;

function addCommonAttachmentStyle(props: AttachmentBaseStyleProps) {
  return css`
    position: relative;
    width: 158px;
    height: ${props.$hasLargeAttachmentView ? largeAttachmentHeight : attachmentHeight}px;
    flex-grow: 1;
  `;
}

const StyledAttachmentButton = styled(ChromelessButton)<AttachmentBaseStyleProps>`
  ${(p) => addCommonAttachmentStyle(p)};
  ${(p) =>
    p.$newMultiAttachmentStyle &&
    css`
      ${commonMultiAttachmentStyles};
    `};
  ${(p) =>
    p.$newMultiAttachmentStyle &&
    (!p.$hasLargeAttachmentView || !p.$hasError) &&
    css`
      &:hover {
        background: ${p.theme.alphas.black40};
      }
      &:active {
        background: ${p.theme.alphas.black30};
      }
    `};
`;

export const StyledAttachmentDiv = styled.div<AttachmentBaseStyleProps>`
  ${(p) => addCommonAttachmentStyle(p)};
  ${(p) =>
    p.$newMultiAttachmentStyle &&
    css`
      ${commonMultiAttachmentStyles};
    `};
`;

interface ProgressStyleProps {
  $progress: number;
}
const StyledProgressDiv = styled.div<ProgressStyleProps>`
  position: absolute;
  left: 0;
  bottom: 0;
  width: ${(p) => p.$progress}%;
  height: 2px;

  background-color: ${(p) => p.theme.palette.blue.shade40};
`;

const infiniteProgressAnimation = keyframes`
  0%
  {
    left: -50%;
  }

  100%
  {
    left: 100%;
  }
`;

const StyledInfiniteProgressDiv = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 50%;
  height: 2px;

  background-color: ${(p) => p.theme.palette.blue.shade40};
  animation: ${infiniteProgressAnimation} 2s linear infinite;
`;

const transition = '300ms ease';

const largeAttachmentViewBottom = 7;

const StyledNameDiv = styled.div<AttachmentBaseStyleProps>`
  position: absolute;
  left: ${(p) => (p.$hasLargeAttachmentView ? 8 : 7)}px;
  right: 10px;
  padding-left: 23px;
  bottom: ${(p) => (p.$hasLargeAttachmentView ? largeAttachmentViewBottom : 21)}px;

  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  line-height: ${attachmentLineHeight}px;

  max-height: ${attachmentLineHeight * attachmentMaxMultiLineRows}px;

  /* This enables multi-line ellipsis. Only works on Chromium and firefox based browsers */
  display: -webkit-box;
  overflow-wrap: break-word;
  white-space: normal;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;

  ${(p) =>
    p.$hasLargeAttachmentView &&
    css`
      transition: bottom ${transition};
    `};

  ${(p) =>
    !p.$hasLargeAttachmentView &&
    css`
      display: block;
      ${ellipsis()}
    `};

  ${StyledAttachmentButton} > && {
    ${(p) =>
      p.$hasLargeAttachmentView &&
      p.$hasError &&
      css`
        bottom: 29px;
        -webkit-line-clamp: ${attachmentMaxMultiLineRows};
      `}
  }

  ${StyledAttachmentButton}:hover > && {
    ${(p) => addNameDivButtonHoverStyles(p)}
  }

  ${StyledAttachmentButton}:active > && {
    right: 10px;
    bottom: ${(p) => (p.$hasLargeAttachmentView ? largeAttachmentViewBottom : 21)}px;
    -webkit-line-clamp: 1;
    ${(p) =>
      p.$hasLargeAttachmentView &&
      css`
        transition-duration: 0ms;
      `};
  }
`;

function addNameDivButtonHoverStyles(p: AttachmentBaseStyleProps) {
  if (p.$hasLargeAttachmentView && !p.$hasError) {
    return css`
      bottom: 29px;
      -webkit-line-clamp: ${attachmentMaxMultiLineRows};
    `;
  }

  if (p.$hasLargeAttachmentView && p.$hasError) {
    return css`
      bottom: ${largeAttachmentViewBottom}px;
      -webkit-line-clamp: ${attachmentMaxMultiLineRows - 1};
    `;
  }

  return css`
    right: 40px;
  `;
}

interface StyledErrorSpanProps {
  $isTooltip: boolean;
}

const StyledErrorSpan = styled.span<StyledErrorSpanProps>`
  color: ${(p) => (p.$isTooltip ? `` : p.theme.palette.red.shade50)};
`;

const StyledIconDiv = styled.div`
  position: absolute;
  left: 0;
  top: 1px;
  width: 16px;
  height: 16px;
`;

const StyledButton = styled(ChromelessButton)<AttachmentBaseStyleProps>`
  display: flex;
  position: absolute;
  bottom: ${(p) => (p.$hasLargeAttachmentView ? 8 : 11)}px;
  right: ${(p) => (p.$hasLargeAttachmentView ? 8 : 11)}px;
  height: 24px; /* Needed for Safari */

  opacity: 0;
  ${(p) =>
    p.$hasLargeAttachmentView &&
    css`
      transition: opacity ${transition};
    `};

  ${StyledAttachmentButton}:hover & {
    opacity: 1;
  }

  ${StyledAttachmentButton}:active & {
    opacity: 0;
    ${(p) =>
      p.$hasLargeAttachmentView &&
      css`
        transition-duration: 0ms;
      `};
  }
`;

const gifButtonSpacing = 12;

const StyledLargeGifViewButton = styled(StyledButton)`
  bottom: auto;
  top: ${gifButtonSpacing}px;
  right: ${gifButtonSpacing}px;

  /* Remove transition to match small attachments that don't have any transition. */
  transition-property: none;

  /*
    This makes the icon background white i.e. remove button has a white X instead of a transparent X.
    This renders behind the icon if the icon is "position: relative" (see StyledRelativeIcon below).
  */
  &::before {
    content: '';

    position: absolute;
    top: 4px;
    left: 4px;
    width: 16px;
    height: 16px;

    background: ${(p) => p.theme.foreground};
    border-radius: 100%;
  }
`;

const StyledRelativeIcon = styled(Icon)`
  position: relative;
`;

function addFullAbsolutePositionStyles() {
  return css`
    position: absolute;
    width: 100%;
    height: 100%;
  `;
}

const StyledLargeGifViewImg = styled.img`
  background-color: ${(p) => p.theme.greys.shade30};
  object-fit: cover;

  ${addFullAbsolutePositionStyles()}
`;

const StyledLargeGifViewSkeleton = styled(Skeleton)`
  ${addFullAbsolutePositionStyles()}
`;

const StyledLargeGifHoverOverlay = styled.div`
  ${addFullAbsolutePositionStyles()}

  background-color: transparent;
  &:hover {
    background-color: ${(p) => p.theme.alphas.black40};
  }
  &:active {
    background-color: transparent;
  }
`;

const StyledSizeOrRemoteSourceDiv = styled.div<AttachmentBaseStyleProps>`
  position: absolute;
  left: 30px;
  margin-right: 4px;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  color: ${(p) => p.theme.greys.shade70};
  text-transform: capitalize;

  ${(p) => addSizeDivPlacementStyles(p)};
`;

function addSizeDivPlacementStyles(p: AttachmentBaseStyleProps) {
  if (p.$hasLargeAttachmentView && p.$hasError) {
    return css`
      bottom: 10px;
      opacity: 1;
      transition: opacity ${transition};
      ${StyledAttachmentButton}:hover > && {
        opacity: 0;
      }
      ${StyledAttachmentButton}:active > && {
        opacity: 1;
        transition-duration: 0ms;
      }
    `;
  }

  if (p.$hasLargeAttachmentView) {
    return css`
      bottom: 10px;
      opacity: 0;
      transition: opacity ${transition};
      ${StyledAttachmentButton}:hover > && {
        opacity: 1;
      }
      ${StyledAttachmentButton}:active > && {
        opacity: 0;
        transition-duration: 0ms;
      }
    `;
  }

  return css`
    bottom: 5px;
  `;
}

export const StyledImagePreviewWrapper = styled.div`
  display: flex;
`;

const StyledThumbnailImage = styled.img`
  position: absolute;
  object-fit: cover;
  width: 100%;
  height: 100%;
  opacity: 1;

  transition: opacity ${transition};
  ${StyledAttachmentButton}:hover > && {
    opacity: 0;
  }
  ${StyledAttachmentButton}:active > && {
    opacity: 1;
    transition-duration: 0ms;
  }
`;

const StyledThumbnailPlaceholderImage = styled.img`
  position: absolute;
  top: 28px;
  left: 0;
  right: 0;
  opacity: 1;
  margin: auto;

  transition: opacity ${transition};
  ${StyledAttachmentButton}:hover > && {
    opacity: 0;
  }
  ${StyledAttachmentButton}:active > && {
    opacity: 1;
    transition-duration: 0ms;
  }
`;

const StyledTooltipMessageDiv = styled.div`
  max-width: 400px;
  overflow-wrap: break-word;
  white-space: normal;
`;

function thumbnailOverlayStyles(shade: string) {
  return css`
    position: absolute;
    top: 69px;
    bottom: 0;
    width: 100%;
    background: linear-gradient(
      180deg,
      ${transparentize(1, shade)} 0%,
      ${transparentize(0.9913528, shade)} 6.67%,
      ${transparentize(0.96449, shade)} 13.33%,
      ${transparentize(0.9183401, shade)} 20%,
      ${transparentize(0.852589, shade)} 26.67%,
      ${transparentize(0.768225, shade)} 33.33%,
      ${transparentize(0.668116, shade)} 40%,
      ${transparentize(0.557309, shade)} 46.67%,
      ${transparentize(0.442691, shade)} 53.33%,
      ${transparentize(0.331884, shade)} 60%,
      ${transparentize(0.231775, shade)} 66.67%,
      ${transparentize(0.147411, shade)} 73.33%,
      ${transparentize(0.08166, shade)} 80%,
      ${transparentize(0.03551, shade)} 86.67%,
      ${transparentize(0.008647, shade)} 93.33%,
      ${shade} 100%
    );
  `;
}

const StyledThumbnailOverlayDiv = styled.div`
  ${(p) => thumbnailOverlayStyles(p.theme.greys.white)}
`;

const StyledThumbnailOverlayHoverDiv = styled.div`
  ${(p) => thumbnailOverlayStyles(p.theme.greys.shade20)}
  opacity: 0;
  transition: opacity ${transition};
  ${StyledAttachmentButton}:hover > && {
    opacity: 1;
  }
  ${StyledAttachmentButton}:active > && {
    opacity: 0;
    transition-duration: 0ms;
  }
`;

/*
 * Component.
 */

interface AttachmentInProgressState {
  status: DownloadStatusesEnum.IN_PROGRESS;
  cancelTokenSource: CancelTokenSource;
  progress: number;
}

interface AttachmentFailedState {
  status: DownloadStatusesEnum.FAILED;
}

interface AttachmentSucceededState {
  status: DownloadStatusesEnum.SUCCEEDED;
  filepath?: string;
}

type AttachmentState = AttachmentInProgressState | AttachmentFailedState | AttachmentSucceededState;

export const AttachmentBase: FC<AttachmentBaseProps> = (props) => {
  const theme = useUserTheme();
  const [state, setState] = useState<AttachmentState>();
  const buildUnmountCancelToken = useLazyUnmountCancelToken();
  const {
    attachment,
    newMultiAttachmentStyle,
    isInteractionDisabled,
    shouldDisablePreview,
    customErrorMessage,
  } = props;
  const company = useAppSelector(getCompany);
  const nameRef = useRef<HTMLDivElement>(null);
  const [isTooltipEnabled, setTooltipEnabled] = useState(false);

  const isRemoteAttachment = attachment.status === AttachmentStatusesEnum.REMOTE;

  useLayoutEffect(() => {
    if (!nameRef.current) {
      return;
    }

    // Single line overflow check for attachments without previews
    if (!props.hasLargeAttachmentView) {
      setTooltipEnabled(nameRef.current.offsetWidth < nameRef.current.scrollWidth);
    }
    // Multi line overflow check if it exceeds the max height specified from the line height * max number of rows
    else {
      setTooltipEnabled(nameRef.current.scrollHeight > attachmentMaxMultiLineRows * attachmentLineHeight);
    }
  }, [setTooltipEnabled, props.hasLargeAttachmentView]);

  /*
   * Event handlers.
   */

  const downloadOrPreviewAsync = async (
    downloadAsync: (options: PreviewAttachmentOptions) => Promise<string | void>,
  ) => {
    if (state && state.status !== DownloadStatusesEnum.SUCCEEDED) {
      return;
    }

    try {
      // Create a new cancel token that we combine with the component-level unmount token.
      const cancelTokenSource = buildCancelTokenSource();
      const cancelToken = combineCancelTokens(buildUnmountCancelToken(), cancelTokenSource.token);

      // Handle progress updates.
      const onProgress: (arg: number) => void = (progress) => {
        setState({status: DownloadStatusesEnum.IN_PROGRESS, cancelTokenSource, progress});
      };

      // Update the state and download.
      setState({status: DownloadStatusesEnum.IN_PROGRESS, cancelTokenSource, progress: 0});
      const filepath = await downloadAsync({cancelToken, onProgress});
      setState({status: DownloadStatusesEnum.SUCCEEDED, filepath: filepath || undefined});
    } catch (error) {
      if (error instanceof CancelError) {
        setState(undefined);
        return;
      }

      reportToBugsnag(error);
      setState({status: DownloadStatusesEnum.FAILED});
    }
  };

  const {onPreviewClickAsync} = props;
  const onPreviewButtonClick: ClickEventHandler = (event) => {
    // Nothing to do if the event was already handled, interactions are disabled or previews are disabled
    if (event.defaultPrevented || shouldDisablePreview) {
      return;
    }

    downloadOrPreviewAsync(onPreviewClickAsync);
  };

  const {onDownloadClickAsync} = props;
  const onDownloadButtonClick: ClickEventHandler = (event) => {
    event.preventDefault();

    downloadOrPreviewAsync(async (options) => {
      const {filepath} = await onDownloadClickAsync({
        ...options,
        shouldPromptForPath: event.shiftKey,
      });
      return filepath;
    });
  };

  const onExternalLinkClick: ClickEventHandler = (event) => {
    event.preventDefault();
    if (isRemoteAttachment) {
      openInBrowser(attachment.remotePath);
    }
  };

  const {onCancelUploadClick, onRemoveClick} = props;
  const onRemoveButtonClick: ClickEventHandler = (event) => {
    event.preventDefault();

    const isDraft = attachment.status === AttachmentStatusesEnum.DRAFT;
    const onRemoveClickHandler = isDraft ? onCancelUploadClick : onRemoveClick;
    if (!onRemoveClickHandler) {
      return;
    }

    onRemoveClickHandler();
  };

  const onCancelButtonClick: ClickEventHandler = (event) => {
    event.preventDefault();

    if (!state || state.status !== DownloadStatusesEnum.IN_PROGRESS) {
      throw new Error('No download in progress.');
    }

    state.cancelTokenSource.cancel();
  };

  /*
   * Render.
   */

  const {hasError, canBeRemoved} = props;

  const renderButton = () =>
    maybeRenderRemoveButton() ||
    maybeRenderCancelButton() ||
    maybeRenderDownloadButton() ||
    maybeRenderExternalLinkButton();

  const ButtonComponent =
    attachment.giphyId !== undefined && props.hasLargeAttachmentView
      ? StyledLargeGifViewButton
      : StyledButton;

  const maybeRenderDownloadButton = () => {
    // These actions are only available for uploaded attachments.
    if (attachment.status !== AttachmentStatusesEnum.UPLOADED || hasError) {
      return null;
    }

    return (
      <ButtonComponent
        $hasLargeAttachmentView={props.hasLargeAttachmentView}
        $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
        shouldPreventMouseDown
        onClick={onDownloadButtonClick}
      >
        <Icon
          name="downloadCircle"
          color={theme.message.attachmentButton}
          hoverColor={theme.message.attachmentButtonHover}
        />
        <VisuallyHidden>
          <Trans id="attachment-download">Download</Trans>
        </VisuallyHidden>
      </ButtonComponent>
    );
  };

  const maybeRenderExternalLinkButton = () => {
    if (!isRemoteAttachment) {
      return null;
    }

    return (
      <ButtonComponent
        $hasLargeAttachmentView={props.hasLargeAttachmentView}
        $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
        shouldPreventMouseDown
        onClick={onExternalLinkClick}
      >
        <Icon
          name="externalLinkCircle"
          color={theme.message.attachmentButton}
          hoverColor={theme.message.attachmentButtonHover}
        />
        <VisuallyHidden>
          <Trans id="attachment-external-link">Open external link</Trans>
        </VisuallyHidden>
      </ButtonComponent>
    );
  };

  const maybeRenderCancelButton = () => {
    if (!state || state.status !== DownloadStatusesEnum.IN_PROGRESS) {
      return null;
    }

    return (
      <ButtonComponent
        $hasLargeAttachmentView={props.hasLargeAttachmentView}
        $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
        shouldPreventMouseDown
        onClick={onCancelButtonClick}
      >
        <Icon
          name="crossCircle"
          color={theme.message.attachmentButton}
          hoverColor={theme.message.attachmentButtonHover}
        />
        <VisuallyHidden>
          <Trans id="attachment-cancel">Cancel upload</Trans>
        </VisuallyHidden>
      </ButtonComponent>
    );
  };

  const maybeRenderRemoveButton = () => {
    // We may not need to display this button.
    if (!canBeRemoved) {
      return null;
    }

    const color = hasError ? theme.palette.red.shade40 : theme.message.attachmentButton;
    const hoverColor = hasError ? theme.palette.red.shade50 : theme.message.attachmentButtonHover;

    return (
      <ButtonComponent
        $hasLargeAttachmentView={props.hasLargeAttachmentView}
        $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
        shouldPreventMouseDown
        onClick={onRemoveButtonClick}
      >
        <StyledRelativeIcon name="crossCircle" color={color} hoverColor={hoverColor} />
        <VisuallyHidden>
          <Trans id="attachment-remove">Remove</Trans>
        </VisuallyHidden>
      </ButtonComponent>
    );
  };

  const maybeRenderErrorPrefix = (isTooltip: boolean) => {
    if (!hasError) {
      return null;
    }

    return (
      <StyledErrorSpan $isTooltip={isTooltip}>
        [{customErrorMessage ?? <Trans id="attachment-uploaderror">Failed to upload</Trans>}]&nbsp;
      </StyledErrorSpan>
    );
  };

  const maybeRenderProgress = () => {
    // If this is a draft attachment, we may need to render the upload progress.
    const draftProgress = attachment.status === AttachmentStatusesEnum.DRAFT && attachment.progress;
    if (draftProgress && draftProgress > 0) {
      // DraftProgress runs between 0 and 100.
      return <StyledProgressDiv $progress={draftProgress} />;
    }

    // If we're currently downloading, we may need to render the download progress.
    const downloadProgress = state && state.status === DownloadStatusesEnum.IN_PROGRESS && state.progress;
    if (downloadProgress && downloadProgress > 0) {
      // DownloadProgress runs between 0 and 1.
      return <StyledProgressDiv $progress={downloadProgress * 100} />;
    }

    // Show the infinite loader for file types that are in a loading state
    if (
      attachment.status === AttachmentStatusesEnum.FILE &&
      attachment.uploadStatus === AsyncStatusesEnum.LOADING
    ) {
      return <StyledInfiniteProgressDiv />;
    }

    return null;
  };

  const maybeRenderThumbnail = () => {
    // If there's an error in large view, we show name/size, not a thumbnail.
    // We don't show the thumbnail in the small view ever.
    if (hasError || !props.hasLargeAttachmentView) {
      return null;
    }

    return props.attachment.hasThumbnail ? (
      <StyledThumbnailImage
        alt="attachment-thumbnail"
        src={`${company.url}/attachments/${props.attachment.uid}?action=thumbnail`}
      />
    ) : (
      <StyledThumbnailPlaceholderImage
        alt="attachment-thumbnail-placeholder"
        src={thumbnailPlaceholderImageContent}
      />
    );
  };

  const maybeRenderSize = () => {
    if (isRemoteAttachment || attachment.size === 0) {
      return null;
    }

    return (
      <StyledSizeOrRemoteSourceDiv
        $hasError={hasError}
        $hasLargeAttachmentView={props.hasLargeAttachmentView}
        $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
      >
        <FileSizeFormatter value={attachment.size} />
      </StyledSizeOrRemoteSourceDiv>
    );
  };

  const maybeRenderRemoteAttachmentSource = () => {
    if (!isRemoteAttachment) {
      return null;
    }

    return (
      <StyledSizeOrRemoteSourceDiv
        $hasError={hasError}
        $hasLargeAttachmentView={props.hasLargeAttachmentView}
        $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
      >
        {computeRemoteAttachmentSourceName(attachment)}
      </StyledSizeOrRemoteSourceDiv>
    );
  };

  const renderAttachmentContent = () => (
    <>
      {maybeRenderThumbnail()}
      {!hasError && <StyledThumbnailOverlayDiv />}
      {!hasError && <StyledThumbnailOverlayHoverDiv />}
      <StyledNameDiv
        $hasError={hasError}
        $hasLargeAttachmentView={props.hasLargeAttachmentView}
        $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
        ref={nameRef}
      >
        <StyledIconDiv>
          <AttachmentFileIcon contentType={visualContentType} />
        </StyledIconDiv>
        {maybeRenderErrorPrefix(false)}
        {name}
        <TooltipExplicitCondition isEnabled={isTooltipEnabled} />
      </StyledNameDiv>
      {maybeRenderSize()}
      {maybeRenderRemoteAttachmentSource()}
      {maybeRenderProgress()}
      {!isInteractionDisabled && renderButton()}
    </>
  );

  function renderAttachment() {
    // Show large Giphy GIF view if there's no error.
    if (
      attachment.giphyId &&
      !hasError &&
      props.hasLargeAttachmentView &&
      attachment.visualContentType === AttachmentVisualContentTypesEnum.IMAGE &&
      attachmentPreviewableImageTypes.includes(attachment.contentType)
    ) {
      return (
        <StyledAttachmentButton
          className={className}
          $hasError={hasError}
          $hasLargeAttachmentView={props.hasLargeAttachmentView}
          $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
          onClick={onPreviewButtonClick}
          interactionId={`attachment-base-${attachment.uid}`}
        >
          {attachment.status === AttachmentStatusesEnum.UPLOADED ? (
            <StyledLargeGifViewImg src={attachment.url} />
          ) : (
            <StyledLargeGifViewSkeleton />
          )}
          <StyledLargeGifHoverOverlay>{renderButton()}</StyledLargeGifHoverOverlay>
        </StyledAttachmentButton>
      );
    }

    // Show image preview.
    if (
      newMultiAttachmentStyle &&
      attachment.status === AttachmentStatusesEnum.UPLOADED &&
      attachment.visualContentType === AttachmentVisualContentTypesEnum.IMAGE &&
      attachmentPreviewableImageTypes.includes(attachment.contentType)
    ) {
      return (
        <StyledImagePreviewWrapper>
          <CommentViewerSingleImageAttachmentContainer
            attachment={attachment}
            newMultiAttachmentStyle={newMultiAttachmentStyle}
            isInteractionDisabled={isInteractionDisabled}
          />
        </StyledImagePreviewWrapper>
      );
    }

    if (isInteractionDisabled) {
      return (
        <StyledAttachmentDiv
          className={className}
          $hasLargeAttachmentView={props.hasLargeAttachmentView}
          $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
          data-testid={`attachment-base-${attachment.uid}`}
        >
          {renderAttachmentContent()}
        </StyledAttachmentDiv>
      );
    }

    return (
      <StyledAttachmentButton
        className={className}
        $hasError={hasError}
        $hasLargeAttachmentView={props.hasLargeAttachmentView}
        $newMultiAttachmentStyle={props.newMultiAttachmentStyle}
        onClick={onPreviewButtonClick}
        interactionId={`attachment-base-${attachment.uid}`}
      >
        {renderAttachmentContent()}
      </StyledAttachmentButton>
    );
  }

  const {className, shouldDisplayErrorMessageInTooltip} = props;
  const {name, visualContentType} = attachment;

  return (
    <StyledTooltipCoordinator
      customMargin={4}
      render={() => (
        <StyledTooltipMessageDiv>
          {shouldDisplayErrorMessageInTooltip && maybeRenderErrorPrefix(true)}
          {name}
        </StyledTooltipMessageDiv>
      )}
      placement="top"
    >
      <StyledAttachmentDragSource attachment={attachment}>{renderAttachment()}</StyledAttachmentDragSource>
    </StyledTooltipCoordinator>
  );
};
